import { useTranslation } from '@hbs-us/ui';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * "Location is off" — the one thing the punch screen never said.
 *
 * WHAT WAS WRONG. A crew member who pressed Block on the location prompt once punches in and out
 * for the rest of the year with no position on any of it, and the screen looked identical to one
 * that was recording every punch. Nobody in the office could tell a blocked browser from a crew
 * member who never left the yard, and the person holding the phone had no reason to think anything
 * was missing.
 *
 * IT ASKS NOTHING AND BLOCKS NOTHING. Reading `navigator.permissions` is a query, not a prompt, so
 * this cannot spend the one sticky ask that `useBrowserPosition` deliberately saves for the moment
 * somebody types an address. The punch tiles below are untouched: a punch with no location is still
 * a punch, and refusing it would turn a degraded record into a lost shift.
 *
 * TWO LINES, AND NOT ONE MORE. An earlier explanatory panel was rejected. What is left is the
 * consequence and the fix. The browser is not named, because the crew are on four of them.
 */

/**
 * Whether the last real position request came back denied, as a store rather than a variable.
 *
 * Safari shipped `navigator.permissions` late, and on a browser without it the only evidence we
 * have is what `getCurrentPosition` already answered — so `useBrowserPosition` reports its outcome
 * here.
 *
 * ⚠ IT HAS TO BE A STORE. Reading a module variable once inside an empty-dependency effect meant
 * the only denial this could ever see was one recorded BEFORE the punch screen mounted, and the
 * ask happens after: somebody types an address, the combobox requests a position, the browser
 * refuses. The notice stayed hidden for the rest of the session, on exactly the browsers that
 * needed the fallback.
 */
let lastPositionDenied = false;
const listeners = new Set<() => void>();

/** Called by `useBrowserPosition` after every position attempt. */
export function noteGeolocationDenied(denied: boolean): void {
  if (lastPositionDenied === denied) return;
  lastPositionDenied = denied;
  for (const notify of listeners) notify();
}

function subscribeToPositionOutcome(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

const readPositionOutcome = (): boolean => lastPositionDenied;

export function LocationOffNotice() {
  const { t } = useTranslation();
  /** `null` while the Permissions API has not answered — or cannot. Then the fallback decides. */
  const [permissionDenied, setPermissionDenied] = useState<boolean | null>(null);
  const positionDenied = useSyncExternalStore(
    subscribeToPositionOutcome,
    readPositionOutcome,
    readPositionOutcome,
  );

  useEffect(() => {
    let live = true;
    let permission: PermissionStatus | null = null;

    if (typeof navigator === 'undefined' || typeof navigator.permissions?.query !== 'function') {
      return;
    }

    const apply = () => {
      if (live && permission) setPermissionDenied(permission.state === 'denied');
    };

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (!live) return;
        permission = result;
        apply();
        // Somebody who fixes the setting in another tab should not have to reload to see this go.
        permission.onchange = apply;
      })
      // A browser that has the API but refuses the descriptor tells us nothing either way, so the
      // answer stays with the fallback rather than becoming a confident "location is fine".
      .catch(() => {});

    return () => {
      live = false;
      if (permission) permission.onchange = null;
    };
  }, []);

  if (!(permissionDenied ?? positionDenied)) return null;

  /*
    The tinted warning surface is the app's own (`features/jobs/DuplicateSitesBanner`), so this
    reads as the same kind of thing rather than as a fourth white card between the site picker and
    the tiles — which is exactly what it looked like, and got skimmed past.

    BOTH LINES ARE INK. The house banner sets its second line in `--muted-foreground`, which on the
    amber tint measures 4.33:1 in the light theme and misses the 4.5:1 AA floor. Weight carries the
    hierarchy here instead, and both lines clear 17:1.
  */
  return (
    <div
      role="status"
      className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3.5 text-left"
    >
      <p className="flex items-start gap-2 font-medium text-foreground text-sm">
        <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        {t('timesheets:punch.locationOffTitle')}
      </p>
      <p className="mt-1 pl-6 text-foreground text-sm">{t('timesheets:punch.locationOffHow')}</p>
    </div>
  );
}
